import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, UserPlus, Mail, Trash2, Shield, Clock, CheckCircle2,
  AlertCircle, Loader2, Copy, ExternalLink, RefreshCw, Search,
  MoreVertical, Edit2, XCircle, ChevronRight, Home, ArrowRightLeft,
  BookOpen, FileText, Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import HelpIcon from '../components/HelpIcon';
import { getCurrentUserId } from '../utils/currentUser';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
  admin: 'bg-red-500',
  principal_designer: 'bg-purple-500',
  designer: 'bg-blue-500',
  contractor: 'bg-orange-500',
  client: 'bg-green-500'
};

export default function TeamPage() {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('keyMadeDarkMode') === 'true');
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Invite dialog state
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'designer',
    personal_message: ''
  });
  const [inviting, setInviting] = useState(false);
  
  // Succession Planning state
  const [successionData, setSuccessionData] = useState([]);
  const [editingSuccession, setEditingSuccession] = useState(null);
  const [successionForm, setSuccessionForm] = useState({
    successor_id: '',
    knowledge_areas: [],
    transition_notes: ''
  });
  const [savingSuccession, setSavingSuccession] = useState(false);

  const KNOWLEDGE_AREAS = [
    'Project Management',
    'Client Relations',
    'Design Development',
    'Construction Documentation',
    'Procurement',
    'Site Supervision',
    'Budget Management',
    'Vendor Relations',
    'Technical Drawings',
    'Material Selection'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [membersRes, invitationsRes, rolesRes, successionRes] = await Promise.all([
        axios.get(`${API}/api/team/members`),
        axios.get(`${API}/api/team/invitations`),
        axios.get(`${API}/api/team/roles`),
        axios.get(`${API}/api/team/succession-planning`)
      ]);
      setMembers(membersRes.data.members || []);
      setInvitations(invitationsRes.data.invitations || []);
      setRoles(rolesRes.data.roles || []);
      setSuccessionData(successionRes.data || []);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const startEditSuccession = (member) => {
    setEditingSuccession(member.id);
    setSuccessionForm({
      successor_id: member.successor_id || '',
      knowledge_areas: member.knowledge_areas || [],
      transition_notes: member.transition_notes || ''
    });
  };

  const saveSuccession = async (userId) => {
    setSavingSuccession(true);
    try {
      await axios.put(`${API}/api/users/${userId}/succession`, {
        successor_id: successionForm.successor_id || null,
        knowledge_areas: successionForm.knowledge_areas,
        transition_notes: successionForm.transition_notes
      });
      toast.success('Succession planning updated');
      setEditingSuccession(null);
      fetchData();
    } catch (error) {
      console.error('Error saving succession:', error);
      toast.error('Failed to save succession planning');
    } finally {
      setSavingSuccession(false);
    }
  };

  const toggleKnowledgeArea = (area) => {
    setSuccessionForm(prev => ({
      ...prev,
      knowledge_areas: prev.knowledge_areas.includes(area)
        ? prev.knowledge_areas.filter(a => a !== area)
        : [...prev.knowledge_areas, area]
    }));
  };

  const sendInvitation = async () => {
    if (!inviteForm.email || !inviteForm.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setInviting(true);
    try {
      const response = await axios.post(`${API}/api/team/invite`, null, {
        params: {
          email: inviteForm.email,
          role: inviteForm.role,
          personal_message: inviteForm.personal_message || null,
          invited_by: getCurrentUserId()
        }
      });
      
      toast.success('Invitation sent successfully!');
      
      // Copy invite link
      const inviteLink = `${window.location.origin}${response.data.invite_url}`;
      navigator.clipboard.writeText(inviteLink);
      toast.info('Invitation link copied to clipboard');
      
      setShowInviteDialog(false);
      setInviteForm({ email: '', role: 'designer', personal_message: '' });
      fetchData();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const revokeInvitation = async (invitationId) => {
    if (!window.confirm('Revoke this invitation?')) return;
    
    try {
      await axios.delete(`${API}/api/team/invitation/${invitationId}`);
      toast.success('Invitation revoked');
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke invitation');
    }
  };

  const copyInviteLink = (token) => {
    const link = `${window.location.origin}/join?token=${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Invitation link copied');
  };

  const filteredMembers = members.filter(m =>
    !searchQuery ||
    m.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.last_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-[#0A0A0A]' : 'bg-[#FAF9F7]'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-[#BC5D41]" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-6 ${darkMode ? 'bg-[#0A0A0A] text-[#E5E2DD]' : 'bg-[#FAF9F7] text-[#1A1918]'}`}>
      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-sm mb-4" data-testid="breadcrumb-nav">
          <button 
            onClick={() => navigate('/dashboard')}
            className={`flex items-center gap-1 hover:text-[#BC5D41] transition-colors ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}
          >
            <Home className="w-4 h-4" />
            My Desk
          </button>
          <ChevronRight className={`w-4 h-4 ${darkMode ? 'text-[#4A4A4A]' : 'text-gray-400'}`} />
          <span className={`font-medium ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>
            Team Management
          </span>
        </nav>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <HelpIcon elementId="team-management" position="inline" />
              <Users className="w-6 h-6 text-[#BC5D41]" />
              Team Management
            </h1>
            <p className={`text-sm mt-1 ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>
              Manage team members and invitations
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button 
              onClick={() => setShowInviteDialog(true)}
              className="bg-[#BC5D41] hover:bg-[#70170F] text-white"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Team Member
            </Button>
          </div>
        </div>

        <Tabs defaultValue="members" className="space-y-4">
          <TabsList className={darkMode ? 'bg-[#1A1A1A]' : ''}>
            <TabsTrigger value="members">
              <Users className="w-4 h-4 mr-2" />
              Team Members ({members.length})
            </TabsTrigger>
            <TabsTrigger value="succession">
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Succession
            </TabsTrigger>
            <TabsTrigger value="invitations">
              <Mail className="w-4 h-4 mr-2" />
              Pending ({invitations.length})
            </TabsTrigger>
            <TabsTrigger value="roles">
              <Shield className="w-4 h-4 mr-2" />
              Roles
            </TabsTrigger>
          </TabsList>

          {/* Team Members Tab */}
          <TabsContent value="members">
            <Card className={darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : ''}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Active Team Members</CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search members..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={`pl-9 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredMembers.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className={darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}>
                      No team members found
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredMembers.map((member) => (
                      <div 
                        key={member.id}
                        className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-[#1A1A1A]' : 'bg-gray-50'}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${ROLE_COLORS[member.role] || 'bg-gray-500'}`}>
                            {(member.first_name?.[0] || 'U').toUpperCase()}
                          </div>
                          <div>
                            <p className={`font-medium ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                              {member.first_name} {member.last_name}
                            </p>
                            <p className={`text-sm ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
                              {member.email}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="capitalize">
                            {member.role?.replace('_', ' ')}
                          </Badge>
                          {member.is_active ? (
                            <Badge variant="secondary" className="text-emerald-600">Active</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-red-500">Inactive</Badge>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem>
                                <Edit2 className="w-4 h-4 mr-2" />
                                Edit Role
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-red-500">
                                <XCircle className="w-4 h-4 mr-2" />
                                Deactivate
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Succession Planning Tab */}
          <TabsContent value="succession">
            <Card className={darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : ''}>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <ArrowRightLeft className="w-5 h-5 text-[#70170F]" />
                  Succession Planning
                </CardTitle>
                <CardDescription>
                  Assign backups and document knowledge transfer for team members
                </CardDescription>
              </CardHeader>
              <CardContent>
                {successionData.length === 0 ? (
                  <div className="text-center py-8">
                    <ArrowRightLeft className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className={darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}>No team members found</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {successionData.map((member) => (
                      <div 
                        key={member.id}
                        className={`p-4 rounded-lg border ${darkMode ? 'border-[#2A2A2A] bg-[#1A1A1A]' : 'border-[#E5E2DD] bg-[#FAF9F7]'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${ROLE_COLORS[member.role] || 'bg-gray-500'}`}>
                              {member.first_name?.[0]}{member.last_name?.[0]}
                            </div>
                            <div>
                              <h4 className={`font-medium ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                                {member.first_name} {member.last_name}
                              </h4>
                              <p className={`text-sm ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
                                {member.title || member.role}
                              </p>
                            </div>
                          </div>
                          
                          {editingSuccession === member.id ? (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => setEditingSuccession(null)}
                                className={darkMode ? 'border-[#2A2A2A]' : ''}
                              >
                                Cancel
                              </Button>
                              <Button 
                                size="sm"
                                onClick={() => saveSuccession(member.id)}
                                disabled={savingSuccession}
                                className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
                              >
                                {savingSuccession ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                                Save
                              </Button>
                            </div>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => startEditSuccession(member)}
                            >
                              <Edit2 className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                          )}
                        </div>

                        {/* View Mode */}
                        {editingSuccession !== member.id && (
                          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>
                                Successor / Backup
                              </p>
                              {member.successor_name ? (
                                <p className={`text-sm ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                                  {member.successor_name}
                                </p>
                              ) : (
                                <p className={`text-sm italic ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
                                  Not assigned
                                </p>
                              )}
                            </div>
                            <div>
                              <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>
                                Knowledge Areas
                              </p>
                              {member.knowledge_areas?.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {member.knowledge_areas.map((area, idx) => (
                                    <Badge key={idx} variant="secondary" className="text-xs">
                                      {area}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <p className={`text-sm italic ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
                                  Not documented
                                </p>
                              )}
                            </div>
                            {member.transition_notes && (
                              <div className="md:col-span-2">
                                <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>
                                  Transition Notes
                                </p>
                                <p className={`text-sm ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>
                                  {member.transition_notes}
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Edit Mode */}
                        {editingSuccession === member.id && (
                          <div className="mt-4 space-y-4">
                            <div>
                              <Label className={darkMode ? 'text-[#E5E2DD]' : ''}>Successor / Backup</Label>
                              <Select 
                                value={successionForm.successor_id || 'none'} 
                                onValueChange={(val) => setSuccessionForm(prev => ({ ...prev, successor_id: val === 'none' ? '' : val }))}
                              >
                                <SelectTrigger className={`mt-1 ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A] text-[#E5E2DD]' : ''}`}>
                                  <SelectValue placeholder="Select backup person" />
                                </SelectTrigger>
                                <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                                  {/* Radix Select v2 throws on value="" — use a 'none' sentinel */}
                                  <SelectItem value="none" className={darkMode ? 'text-[#E5E2DD]' : ''}>None</SelectItem>
                                  {successionData
                                    .filter(m => m.id !== member.id)
                                    .map(m => (
                                      <SelectItem key={m.id} value={m.id} className={darkMode ? 'text-[#E5E2DD]' : ''}>
                                        {m.first_name} {m.last_name}
                                      </SelectItem>
                                    ))
                                  }
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <Label className={darkMode ? 'text-[#E5E2DD]' : ''}>Knowledge Areas</Label>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {KNOWLEDGE_AREAS.map((area) => (
                                  <Badge 
                                    key={area}
                                    variant={successionForm.knowledge_areas.includes(area) ? 'default' : 'outline'}
                                    className={`cursor-pointer transition-colors ${
                                      successionForm.knowledge_areas.includes(area) 
                                        ? 'bg-[#70170F] hover:bg-[#70170F]/90' 
                                        : darkMode ? 'hover:bg-[#2A2A2A]' : 'hover:bg-gray-100'
                                    }`}
                                    onClick={() => toggleKnowledgeArea(area)}
                                  >
                                    {area}
                                  </Badge>
                                ))}
                              </div>
                            </div>

                            <div>
                              <Label className={darkMode ? 'text-[#E5E2DD]' : ''}>Transition Notes</Label>
                              <Textarea
                                placeholder="Document important knowledge, processes, or handover instructions..."
                                value={successionForm.transition_notes}
                                onChange={(e) => setSuccessionForm(prev => ({ ...prev, transition_notes: e.target.value }))}
                                rows={3}
                                className={`mt-1 ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A] text-[#E5E2DD]' : ''}`}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invitations Tab */}
          <TabsContent value="invitations">
            <Card className={darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : ''}>
              <CardHeader>
                <CardTitle className="text-base">Pending Invitations</CardTitle>
                <CardDescription>
                  Invitations that haven't been accepted yet
                </CardDescription>
              </CardHeader>
              <CardContent>
                {invitations.length === 0 ? (
                  <div className="text-center py-8">
                    <Mail className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className={darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}>
                      No pending invitations
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {invitations.map((invite) => (
                      <div 
                        key={invite.id}
                        className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-[#1A1A1A]' : 'bg-gray-50'}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <Mail className="w-5 h-5 text-gray-600" />
                          </div>
                          <div>
                            <p className={`font-medium ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                              {invite.email}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              Expires {new Date(invite.expires_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize">
                            {invite.role?.replace('_', ' ')}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyInviteLink(invite.token)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => revokeInvitation(invite.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Roles Tab */}
          <TabsContent value="roles">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {roles.map((role) => (
                <Card key={role.id} className={darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : ''}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${ROLE_COLORS[role.id] || 'bg-gray-500'}`} />
                      <CardTitle className="text-base">{role.name}</CardTitle>
                    </div>
                    <CardDescription>{role.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {Object.entries(role.permissions || {}).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between text-sm">
                          <span className={`${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'} capitalize`}>
                            {key.replace(/_/g, ' ')}
                          </span>
                          {value ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-400" />
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className={darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A] text-[#E5E2DD]' : ''}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#BC5D41]" />
              Invite Team Member
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Email Address</Label>
              <Input
                type="email"
                placeholder="colleague@company.com"
                value={inviteForm.email}
                onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                className={`mt-1 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}
              />
            </div>
            
            <div>
              <Label>Role</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(value) => setInviteForm(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger className={`mt-1 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Personal Message (optional)</Label>
              <Textarea
                placeholder="Add a personal note to the invitation..."
                value={inviteForm.personal_message}
                onChange={(e) => setInviteForm(prev => ({ ...prev, personal_message: e.target.value }))}
                className={`mt-1 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={sendInvitation}
              disabled={inviting || !inviteForm.email}
              className="bg-[#BC5D41] hover:bg-[#70170F]"
            >
              {inviting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
